import {
  ContractCategory,
  ContractStatus,
  ContractValuePeriod,
  CreateJurisdictionResponse,
  CurrencyCode,
  JurisdictionType,
} from 'compliance-api/generated';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import * as yup from 'yup';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { countryOptions } from '@/modules/common/constants/countries.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { ContractStatusBadge } from '@/modules/admin-dashboard/components/contractManagement/ContractStatusBadge.tsx';
import { contractCategoryOptions } from '@/modules/admin-dashboard/components/contractManagement/contractCategory.ts';
import { contractStatusOptions } from '@/modules/admin-dashboard/components/contractManagement/contractStatus.ts';
import {
  useCreateJurisdictionMutation,
  useJurisdictionsListQuery,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  ModalContentWrapper,
  ModalProviderContext,
  PopoverMenu,
  Select,
  SelectButtonsNew,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInput,
  TextInputField,
} from 'ui';
import { usePrompt } from 'ui/hooks';
import { ChevronDownIcon, PlusIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

export interface ContractByIDFormValues {
  name: string;
  status: ContractStatus;
  currency_code: CurrencyCode;
  owner_id?: number;
  ownerOption?: SelectPaginateOption<number> | null;
  client_id?: number;
  clientOption?: SelectPaginateOption<number> | null;
  jurisdiction_id?: number;
  jurisdictionOption?: SelectPaginateOption<number> | null;
  category?: ContractCategory | null;
  total_value: string;
  value_period?: ContractValuePeriod | null;
  execution_date: Dayjs | null;
  expiry_date: Dayjs | null;
  notice_period_days: string;
  term_for_convenience: boolean;
  policy_number: string;
  physical_storage_location: string;
  comments: string;
  currency_code_ai_captured: boolean;
  category_ai_captured: boolean;
  total_value_ai_captured: boolean;
  value_period_ai_captured: boolean;
  execution_date_ai_captured: boolean;
  expiry_date_ai_captured: boolean;
  notice_period_days_ai_captured: boolean;
  term_for_convenience_ai_captured: boolean;
  physical_storage_location_ai_captured: boolean;
  policy_number_ai_captured: boolean;
  comments_ai_captured: boolean;
  jurisdiction_ai_captured: boolean;
}

export const normalizeFormattedTotalValue = (value: string) => value.replace(/,/g, '');

const formatTotalValueInput = (value: string) => {
  const normalizedValue = normalizeFormattedTotalValue(value).replace(/[^\d.]/g, '');
  const [integerPart = '', ...decimalParts] = normalizedValue.split('.');
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (!decimalParts.length) {
    return formattedIntegerPart;
  }

  return `${formattedIntegerPart}.${decimalParts.join('')}`;
};

const getTotalValueCaretPosition = (value: string, meaningfulCharsCount: number) => {
  if (meaningfulCharsCount === 0) return 0;

  let charsCount = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/[\d.]/.test(value[index])) {
      charsCount += 1;
    }

    if (charsCount === meaningfulCharsCount) {
      return index + 1;
    }
  }

  return value.length;
};

export type ContractByIDTabName = 'details' | 'parties' | 'contributors' | 'tasks' | 'notes';

export const contractByIdValidationSchema = yup.object({
  name: yup.string().trim().max(255),
  status: yup.mixed<ContractStatus>().required('Status is required'),
  currency_code: yup.mixed<CurrencyCode>().required('Currency is required'),
  owner_id: yup.number().nullable(),
  client_id: yup.number().nullable(),
  jurisdiction_id: yup.number().nullable(),
  category: yup.mixed<ContractCategory>().nullable(),
  total_value: yup
    .string()
    .trim()
    .max(32)
    .test('valid-total-value', 'Total value must be a valid number', value => {
      if (!value) return true;

      const normalizedValue = normalizeFormattedTotalValue(value);
      return /^\d+(\.\d+)?$/.test(normalizedValue);
    }),
  value_period: yup.mixed<ContractValuePeriod>().nullable(),
  execution_date: yup.mixed<Dayjs>().nullable(),
  expiry_date: yup.mixed<Dayjs>().nullable(),
  notice_period_days: yup.string().trim().max(16),
  term_for_convenience: yup.boolean().required(),
  policy_number: yup.string().trim().max(255),
  physical_storage_location: yup.string().trim().max(255),
  comments: yup.string().trim().max(2000),
});

const currencyOptions: SelectOption<CurrencyCode>[] = Object.values(CurrencyCode).map(value => ({
  value,
  label: value.toUpperCase(),
}));

const valuePeriodOptions: SelectOption<ContractValuePeriod>[] = Object.values(
  ContractValuePeriod,
).map(value => ({
  value,
  label: fixNameEnum(value),
}));

const jurisdictionTypeOptions: SelectOption<JurisdictionType>[] = Object.values(
  JurisdictionType,
).map(value => ({
  value,
  label: fixNameEnum(value),
}));

export const getContractByIDInitialValues = (
  contract?: {
    contract: {
      name?: string;
      status: ContractStatus;
      currency_code: CurrencyCode;
      currency_code_ai_captured: boolean;
      owner_id?: number;
      client_id?: number;
      jurisdiction_id?: number;
      jurisdiction_ai_captured: boolean;
      category?: ContractCategory;
      category_ai_captured: boolean;
      total_value?: number;
      total_value_ai_captured: boolean;
      value_period?: ContractValuePeriod;
      value_period_ai_captured: boolean;
      execution_date?: string;
      execution_date_ai_captured: boolean;
      expiry_date?: string;
      expiry_date_ai_captured: boolean;
      notice_period_days?: number;
      notice_period_days_ai_captured: boolean;
      term_for_convenience?: boolean;
      term_for_convenience_ai_captured: boolean;
      policy_number?: string;
      policy_number_ai_captured: boolean;
      physical_storage_location?: string;
      physical_storage_location_ai_captured: boolean;
      comments?: string;
      comments_ai_captured: boolean;
    };
    owner?: { id: number; first_name: string; last_name: string } | undefined;
    jurisdiction?: { id: number; name: string } | undefined;
  },
  clientOption?: SelectPaginateOption<number> | null,
): ContractByIDFormValues => ({
  name: contract?.contract.name || '',
  status: contract?.contract.status || ContractStatus.Draft,
  currency_code: contract?.contract.currency_code || CurrencyCode.Aud,
  owner_id: contract?.contract.owner_id,
  ownerOption: contract?.owner
    ? {
        value: contract.owner.id,
        label: `${contract.owner.first_name} ${contract.owner.last_name}`.trim(),
      }
    : contract?.contract.owner_id
    ? { value: contract.contract.owner_id, label: `User #${contract.contract.owner_id}` }
    : null,
  client_id: contract?.contract.client_id,
  clientOption,
  jurisdiction_id: contract?.contract.jurisdiction_id,
  jurisdictionOption: contract?.jurisdiction
    ? { value: contract.jurisdiction.id, label: contract.jurisdiction.name }
    : contract?.contract.jurisdiction_id
    ? {
        value: contract.contract.jurisdiction_id,
        label: `Jurisdiction #${contract.contract.jurisdiction_id}`,
      }
    : null,
  jurisdiction_ai_captured: contract?.contract.jurisdiction_ai_captured ?? false,
  category: contract?.contract.category ?? null,
  total_value: contract?.contract.total_value
    ? formatTotalValueInput(contract.contract.total_value.toString())
    : '',
  value_period: contract?.contract.value_period ?? null,
  execution_date: contract?.contract.execution_date
    ? dayjs(contract.contract.execution_date)
    : null,
  expiry_date: contract?.contract.expiry_date ? dayjs(contract.contract.expiry_date) : null,
  notice_period_days: contract?.contract.notice_period_days?.toString() || '',
  term_for_convenience: contract?.contract.term_for_convenience ?? false,
  policy_number: contract?.contract.policy_number || '',
  physical_storage_location: contract?.contract.physical_storage_location || '',
  comments: contract?.contract.comments || '',
  currency_code_ai_captured: contract?.contract.currency_code_ai_captured ?? false,
  category_ai_captured: contract?.contract.category_ai_captured ?? false,
  total_value_ai_captured: contract?.contract.total_value_ai_captured ?? false,
  value_period_ai_captured: contract?.contract.value_period_ai_captured ?? false,
  execution_date_ai_captured: contract?.contract.execution_date_ai_captured ?? false,
  expiry_date_ai_captured: contract?.contract.expiry_date_ai_captured ?? false,
  notice_period_days_ai_captured: contract?.contract.notice_period_days_ai_captured ?? false,
  term_for_convenience_ai_captured: contract?.contract.term_for_convenience_ai_captured ?? false,
  physical_storage_location_ai_captured:
    contract?.contract.physical_storage_location_ai_captured ?? false,
  policy_number_ai_captured: contract?.contract.policy_number_ai_captured ?? false,
  comments_ai_captured: contract?.contract.comments_ai_captured ?? false,
});

const fieldLabelClassName =
  'font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase font-medium';

const SelectFieldLabel = ({ label, createdWithAi }: { label: string; createdWithAi?: boolean }) => (
  <div className="mb-2 flex items-center justify-between gap-2">
    <span className={fieldLabelClassName}>{label}</span>
    <CreatedByAiLabel created_with_ai={createdWithAi} />
  </div>
);

export const ContractByIDDetailsTab = ({ readOnly = false }: { readOnly?: boolean }) => {
  const formik = useFormikContext<ContractByIDFormValues>();
  const { openModal } = useContext(ModalProviderContext);
  const [jurisdictionCacheUniq, setJurisdictionCacheUniq] = useState(Math.random());
  const { fetchNextPage } = useJurisdictionsListQuery(
    {
      page: 1,
      page_size: 20,
    },
    true,
  );

  const handleLoadJurisdictions = useCallback(
    async (page: number) => {
      const data = await fetchNextPage({ pageParam: page });
      const jurisdictions = data?.data?.pages?.at(-1)?.data || [];
      return jurisdictions.map(({ id, name }) => ({ value: id, label: name }));
    },
    [fetchNextPage],
  );

  const handleJurisdictionCreated = useCallback(
    (response: CreateJurisdictionResponse) => {
      const option = {
        value: response.jurisdiction.id,
        label: response.jurisdiction.name,
      };

      void formik.setFieldValue('jurisdiction_id', option.value);
      void formik.setFieldValue('jurisdictionOption', option);
      setJurisdictionCacheUniq(Math.random());
    },
    [formik],
  );

  const handleAddNewJurisdiction = useCallback(() => {
    openModal(CreateJurisdictionModal, { onSuccess: handleJurisdictionCreated });
  }, [handleJurisdictionCreated, openModal]);

  const hasUnsavedChanges = !readOnly && formik.dirty && !formik.isSubmitting;
  const leaveMessage =
    'Do you really want to leave this form? You will lose some entered information.';

  usePrompt(hasUnsavedChanges, leaveMessage);

  const blocker = useBlocker(hasUnsavedChanges);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    if (window.confirm(leaveMessage)) {
      blocker.proceed();
      return;
    }

    blocker.reset();
  }, [blocker, leaveMessage]);

  const statusItems = useMemo(
    () =>
      contractStatusOptions.map(({ value }) => ({
        label: <ContractStatusBadge status={value} />,
        onClick: () => {
          void formik.setFieldValue('status', value);
          formik.setFieldError('status', '');
        },
      })),
    [formik],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {readOnly ? (
          <ContractStatusBadge status={formik.values.status} />
        ) : (
          <PopoverMenu
            button={
              <div className="flex items-center gap-2">
                <ContractStatusBadge status={formik.values.status} />
                <img src={ChevronDownIcon} alt="" className="h-4 w-4 shrink-0" />
              </div>
            }
            items={statusItems}
            placement="bottom-start"
            classes={{
              container: 'w-fit',
              panel:
                'w-[220px] p-0 bg-white border border-gray-200 rounded shadow-lg overflow-hidden z-50',
            }}
          />
        )}
      </div>

      <div className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-5">
        <div className="min-w-0 flex flex-col">
          <SelectFieldLabel
            label="Currency"
            createdWithAi={formik.values.currency_code_ai_captured}
          />
          <Select<CurrencyCode>
            options={currencyOptions}
            value={formik.values.currency_code}
            onChange={value => formik.setFieldValue('currency_code', value)}
            showBorderBottom
            disabled={readOnly}
          />
        </div>

        <div className="min-w-0">
          <UsersSelectPaginated
            label="Contract owner"
            isMulti={false}
            value={formik.values.ownerOption || undefined}
            onChange={value => {
              formik.setFieldValue('owner_id', value?.value);
              formik.setFieldValue('ownerOption', value);
            }}
            isClearable
            disabled={readOnly}
          />
        </div>

        <div className="min-w-0 flex flex-col">
          <SelectFieldLabel
            label="Jurisdiction"
            createdWithAi={formik.values.jurisdiction_ai_captured}
          />
          <SelectPaginate
            cacheUniqs={[jurisdictionCacheUniq]}
            value={formik.values.jurisdictionOption || null}
            onChange={value => {
              const option = value as SelectPaginateOption<number> | null;
              formik.setFieldValue('jurisdiction_id', option?.value);
              formik.setFieldValue('jurisdictionOption', option);
            }}
            onAsync={handleLoadJurisdictions}
            isClearable
            addedLastOption={
              readOnly
                ? undefined
                : {
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={PlusIcon} alt="" className="h-4 w-4" />
                        <span>Add new Jurisdiction</span>
                      </div>
                    ),
                    onClick: handleAddNewJurisdiction,
                  }
            }
            disabled={readOnly}
          />
        </div>

        <div className="min-w-0 flex flex-col">
          <SelectFieldLabel label="Category" createdWithAi={formik.values.category_ai_captured} />
          <Select<ContractCategory, false, true>
            options={contractCategoryOptions}
            value={formik.values.category}
            onChange={value => formik.setFieldValue('category', value)}
            isClearable
            disabled={readOnly}
          />
        </div>

        <TextInput
          name="total_value"
          label="Total value"
          labelButton={<CreatedByAiLabel created_with_ai={formik.values.total_value_ai_captured} />}
          placeholder="0"
          className="min-w-0"
          value={formik.values.total_value}
          error={formik.errors.total_value}
          inputMode="decimal"
          disabled={readOnly}
          onChange={event => {
            const input = event.currentTarget;
            const cursorPosition = input.selectionStart ?? input.value.length;
            const meaningfulCharsBeforeCursor = input.value
              .slice(0, cursorPosition)
              .replace(/[^\d.]/g, '').length;
            const formattedValue = formatTotalValueInput(input.value);
            const nextCursorPosition = getTotalValueCaretPosition(
              formattedValue,
              meaningfulCharsBeforeCursor,
            );

            void formik.setFieldValue('total_value', formattedValue);
            formik.setFieldError('total_value', '');

            window.requestAnimationFrame(() => {
              input.setSelectionRange(nextCursorPosition, nextCursorPosition);
            });
          }}
        />

        <div className="min-w-0 flex flex-col">
          <SelectFieldLabel
            label="Value period"
            createdWithAi={formik.values.value_period_ai_captured}
          />
          <Select<ContractValuePeriod, false, true>
            options={valuePeriodOptions}
            value={formik.values.value_period || undefined}
            onChange={value => formik.setFieldValue('value_period', value)}
            isClearable
            disabled={readOnly}
          />
        </div>

        <CalendarInput
          label="Execution date"
          id={'execution_date'}
          classNameLabel="flex justify-between"
          labelButton={
            <CreatedByAiLabel created_with_ai={formik.values.execution_date_ai_captured} />
          }
          value={formik.values.execution_date as any}
          onChange={value => formik.setFieldValue('execution_date', value)}
          disabled={readOnly}
        />

        <CalendarInput
          label="Expiry date"
          classNameLabel="flex justify-between"
          labelButton={<CreatedByAiLabel created_with_ai={formik.values.expiry_date_ai_captured} />}
          value={formik.values.expiry_date as any}
          onChange={value => formik.setFieldValue('expiry_date', value)}
          disabled={readOnly}
        />

        <ClientsSelectPaginated
          label="Client (optional)"
          isMulti={false}
          value={formik.values.clientOption || undefined}
          onChange={value => {
            formik.setFieldValue('client_id', value?.value);
            formik.setFieldValue('clientOption', value);
          }}
          isClearable
          disabled={readOnly}
        />

        <TextInputField
          name="physical_storage_location"
          label="Physical storage location"
          labelButton={
            <CreatedByAiLabel
              created_with_ai={formik.values.physical_storage_location_ai_captured}
            />
          }
          className="min-w-0"
          disabled={readOnly}
        />

        <TextInputField
          name="policy_number"
          label="Policy number"
          labelButton={
            <CreatedByAiLabel created_with_ai={formik.values.policy_number_ai_captured} />
          }
          className="min-w-0"
          disabled={readOnly}
        />

        <TextInputField
          name="notice_period_days"
          label="Notice period"
          labelButton={
            <div className="flex items-center gap-2">
              <span className="normal-case">Days</span>
              <CreatedByAiLabel created_with_ai={formik.values.notice_period_days_ai_captured} />
            </div>
          }
          type="number"
          className="min-w-0"
          disabled={readOnly}
        />

        <div className="col-span-2 min-w-0">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className={fieldLabelClassName}>Allow termination for convenience</span>
            <CreatedByAiLabel created_with_ai={formik.values.term_for_convenience_ai_captured} />
          </div>
          <SelectButtonsNew<boolean>
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            value={formik.values.term_for_convenience}
            onChange={value => formik.setFieldValue('term_for_convenience', value)}
            capitalize={false}
            disabled={readOnly}
          />
        </div>

        <div className="col-span-2 min-w-0">
          <CommentsField createdWithAi={formik.values.comments_ai_captured} readOnly={readOnly} />
        </div>
      </div>
    </div>
  );
};

const CommentsField = ({
  createdWithAi,
  readOnly,
}: {
  createdWithAi: boolean;
  readOnly: boolean;
}) => {
  const formik = useFormikContext<ContractByIDFormValues>();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const maxHeight = 350;

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [formik.values.comments, resizeTextarea]);

  return (
    <div>
      <SelectFieldLabel label="Comments" createdWithAi={createdWithAi} />
      <textarea
        ref={textareaRef}
        name="comments"
        value={formik.values.comments}
        onChange={event => {
          if (readOnly) return;
          formik.setFieldValue('comments', event.target.value);
          formik.setFieldTouched('comments', true, false);
          resizeTextarea();
        }}
        onInput={resizeTextarea}
        className="min-h-[120px] w-full resize-none rounded border border-gray-900 border-opacity-16 px-3 py-3 text-sm leading-[22px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-opacity-100"
        placeholder="Type something"
        disabled={readOnly}
      />
    </div>
  );
};

type CreateJurisdictionFormValues = {
  name: string;
  iso_country_code: string;
  type: JurisdictionType | null;
};

interface CreateJurisdictionModalProps extends BaseModalProps {
  onSuccess?: (response: CreateJurisdictionResponse) => void;
}

const createJurisdictionInitialValues: CreateJurisdictionFormValues = {
  name: '',
  iso_country_code: '',
  type: null,
};

const createJurisdictionValidationSchema = yup.object<CreateJurisdictionFormValues>({
  name: yup.string().trim().required('Name is required').max(255),
  iso_country_code: yup
    .string()
    .trim()
    .required('Country is required')
    .length(2, 'Country must be a 2-letter code'),
  type: yup.mixed<JurisdictionType>().nullable(),
});

const CreateJurisdictionModal = ({ open, onClose, onSuccess }: CreateJurisdictionModalProps) => {
  const formik = useFormik<CreateJurisdictionFormValues>({
    initialValues: createJurisdictionInitialValues,
    validationSchema: createJurisdictionValidationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      const response = await createJurisdiction({
        name: values.name.trim(),
        iso_country_code: values.iso_country_code.toUpperCase(),
        type: values.type || undefined,
      });

      onSuccess?.(response);
      formik.resetForm();
      onClose();
    },
  });

  const { mutateAsync: createJurisdiction, isLoading } = useCreateJurisdictionMutation(
    formik.setErrors,
  );

  return (
    <ModalContentWrapper
      title="Add jurisdiction"
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[460px]' }}
    >
      <FormikProvider value={formik}>
        <div className="space-y-6">
          <TextInputField name="name" label="Name" />

          <Select<string, false, true>
            label="Country"
            options={countryOptions}
            value={formik.values.iso_country_code || null}
            onChange={value => {
              void formik.setFieldValue('iso_country_code', value || '');
              formik.setFieldError('iso_country_code', '');
            }}
            isClearable
            showBorderBottom
            error={formik.errors.iso_country_code}
          />

          <Select<JurisdictionType, false, true>
            label="Type"
            options={jurisdictionTypeOptions}
            value={formik.values.type}
            onChange={value => {
              void formik.setFieldValue('type', value);
              formik.setFieldError('type', '');
            }}
            isClearable
            showBorderBottom
            error={formik.errors.type}
          />

          <div className="flex justify-end">
            <Button className="w-fit px-4" onClick={formik.submitForm} loading={isLoading}>
              Confirm
            </Button>
          </div>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
